import { DataContext, DataOptions } from "../../types/data.types";
import { Data } from "../Data";
import { UserProfile } from "./UserProfile";

export interface UserStatus {
  index: number;
  label: string;
}

export interface UserStatuses {
  added: UserStatus;
  invited: UserStatus;
  active: UserStatus;
  inactive: UserStatus;
}

export interface UserLocation {
  locationId: string;
  name: string;
}

export interface UserData {
  id?: string;
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  middleName?: string;
  preferredName?: string;
  company?: string;
  email?: string;
  locationId?: string;
  locationName?: string;
  organizationName?: string;
  isTeamLead?: boolean;
  isOrganizationAdmin?: boolean;
  isBilling?: boolean;
  isInstructor?: boolean;
  hasProfilePicture?: boolean;
  jobTitle?: string;
  proficiencyValue?: number;
  department?: string;
  employeeAttributesCount?: number;
  jobAttributesCount?: number;
  gapAttributesCount?: number;
  numberOfMetMandatoryAndRequiredAttributes?: number;
  totalNumberOfMandatoryAndRequiredAttributesForJob?: number;
  isCompliant?: boolean;
  hasJob?: boolean;
  role?: string;
}

export interface UserOptions extends Partial<DataOptions> {
  data?: UserData;
}

export class User extends Data {
  //Based off EmployeeStatus { Added, Invited, Active, Inactive }
  static status: UserStatuses = {
    added: {
      index: 0,
      label: "Added"
    },
    invited: {
      index: 1,
      label: "Invited"
    },
    active: {
      index: 2,
      label: "Active"
    },
    inactive: {
      index: 3,
      label: "Inactive"
    }
  };

  id!: string;
  employeeId!: string;
  rowId!: string;
  firstName!: string;
  isValidFirstName!: boolean;
  lastName!: string;
  fullName!: string;
  middleName!: string;
  preferredName!: string;
  company!: string;
  email!: string;
  location!: UserLocation;
  organizationName!: string;
  isTeamLead!: boolean;
  isOrganizationAdmin!: boolean;
  isBilling!: boolean;
  isInstructor!: boolean;
  hasProfilePicture!: boolean;
  jobTitle!: string;
  proficiency!: number;
  department!: string;
  employeeAttributesCount!: number;
  jobAttributesCount!: number;
  gapAttributesCount!: number;
  numberOfMetMandatoryAndRequiredAttributes!: number;
  totalNumberOfMandatoryAndRequiredAttributesForJob!: number;
  isCompliant!: boolean;
  hasJob!: boolean;
  status!: UserStatus | null;
  profile!: UserProfile | null;

  [key: string]: unknown;

  constructor(options: UserOptions) {
    super(options as DataOptions);

    if (options.data) this.initData(options.data);
  }

  initData(data: UserData): void {
    if (data.id !== undefined) {
      this.id = data.id;
    }
    this.employeeId = data.employeeId || "";
    this.rowId = data.employeeId || "";
    this.firstName = data.firstName || "";
    this.isValidFirstName = true;
    this.lastName = data.lastName || "";
    this.fullName = data.fullName
      ? data.fullName
      : (data.firstName || "") + " " + (data.lastName || "");
    this.middleName = data.middleName || "";
    this.preferredName = data.preferredName || "";
    this.company = data.company || "";
    this.email = data.email || "";
    if (data.locationId) {
      this.location = { locationId: data.locationId, name: data.locationName || "" };
    }
    this.organizationName = data.organizationName || "";
    this.isTeamLead = data.isTeamLead || false;
    this.isOrganizationAdmin =
      data.isOrganizationAdmin || (data.role ? data.role.includes("Admin") : false);
    this.isBilling = data.isBilling || (data.role ? data.role.includes("Billing") : false);
    this.isInstructor = data.isInstructor || false;
    this.hasProfilePicture = data.hasProfilePicture || false;
    this.jobTitle = data.jobTitle || "";
    this.proficiency = data.proficiencyValue || 0;

    //Not sure if should move to another object?
    this.department = data.department || "";
    this.employeeAttributesCount = data.employeeAttributesCount || 0;
    this.jobAttributesCount = data.jobAttributesCount || 0;
    this.gapAttributesCount = data.gapAttributesCount || 0;
    this.numberOfMetMandatoryAndRequiredAttributes =
      data.numberOfMetMandatoryAndRequiredAttributes || 0;
    this.totalNumberOfMandatoryAndRequiredAttributesForJob =
      data.totalNumberOfMandatoryAndRequiredAttributesForJob || 0;
    this.isCompliant = data.isCompliant || false;
    this.hasJob = data.hasJob || false;
  }

  isActive(): boolean {
    return this.status === User.status.active;
  }

  isMe(user: User): boolean {
    return this.employeeId === user.employeeId;
  }

  updateName(first: string | undefined | null, last: string | undefined | null): void {
    if (first !== undefined && first !== null) this.firstName = first;
    if (last !== undefined && last !== null) this.lastName = last;
    this.fullName = this.firstName + " " + this.lastName;
  }

  loadProfile(
    context: DataContext,
    updateCache?: boolean,
    postData?: Record<string, unknown>
  ): Promise<UserProfile> {
    return new Promise((resolve, reject) => {
      if (this.profile && updateCache !== true) resolve(this.profile);
      else
        this.profile = new UserProfile({
          employeeId: this.employeeId,
          resolve,
          reject,
          context,
          ...(postData && { postData })
        });
    });
  }

  getProfilePictureUrl(date?: Date): string {
    return (
      Data.apiBasePath +
      "/Profile/Avatar?employeeId=" +
      this.employeeId +
      (date ? "&v=" + date.getTime() : "")
    );
  }

  getInitials(): string {
    if (!this.firstName || !this.lastName) return "";
    return this.firstName.substr(0, 1) + this.lastName.substr(0, 1);
  }
}
